import type { App } from 'vue'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'

interface WindowSentry {
  dsn?: string
  environment?: string
  release?: string
}

let initialized = false

export function initSentry(app: App, router: Router): void {
  const envDsnRaw = import.meta.env.VITE_SENTRY_DSN
  const envEnvironmentRaw = import.meta.env.VITE_SENTRY_ENVIRONMENT
  const envReleaseRaw = import.meta.env.VITE_SENTRY_RELEASE
  const winSentry = readWindowSentry()

  const dsn =
    (typeof envDsnRaw === 'string' && envDsnRaw.length > 0 ? envDsnRaw : undefined) ??
    winSentry?.dsn
  if (!dsn) {
    return
  }

  const environment =
    (typeof envEnvironmentRaw === 'string' && envEnvironmentRaw.length > 0 ? envEnvironmentRaw : undefined) ??
    winSentry?.environment ??
    'production'

  const release =
    (typeof envReleaseRaw === 'string' && envReleaseRaw.length > 0 ? envReleaseRaw : undefined) ??
    winSentry?.release

  Sentry.init({
    app,
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    sendDefaultPii: false,
  })

  initialized = true
}

export function setSentryUserId(id: string | number | null | undefined): void {
  if (!initialized) return
  if (id === null || id === undefined || id === '') {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({ id: String(id) })
}

export function clearSentryUser(): void {
  if (!initialized) return
  Sentry.setUser(null)
}

export function isSentryInitialized(): boolean {
  return initialized
}

function readWindowSentry(): WindowSentry | undefined {
  if (typeof globalThis === 'undefined' || typeof globalThis !== 'object') {
    return undefined
  }
  const out: WindowSentry = {}
  if ('sentryDsn' in globalThis) {
    const raw = (globalThis as Record<string, unknown>)['sentryDsn']
    if (typeof raw === 'string') {
      out.dsn = raw
    }
  }
  if ('sentryEnvironment' in globalThis) {
    const raw = (globalThis as Record<string, unknown>)['sentryEnvironment']
    if (typeof raw === 'string') {
      out.environment = raw
    }
  }
  if ('sentryRelease' in globalThis) {
    const raw = (globalThis as Record<string, unknown>)['sentryRelease']
    if (typeof raw === 'string') {
      out.release = raw
    }
  }
  return out
}
